import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Globe, Smartphone, TrendingUp, Zap, Code, Palette } from "lucide-react";

const services = [
  {
    icon: Globe,
    title: "Website Platforms",
    description:
      "Custom-built websites that convert visitors into customers. Fast, responsive, and tailored to your brand.",
    color: "from-[oklch(0.65_0.15_230)] to-[oklch(0.55_0.18_240)]",
  },
  {
    icon: Smartphone,
    title: "Mobile Apps",
    description:
      "Native iOS and Android applications that deliver seamless experiences on any device.",
    color: "from-[oklch(0.58_0.22_345)] to-[oklch(0.68_0.20_335)]",
  },
  {
    icon: TrendingUp,
    title: "Marketing Automation",
    description:
      "Intelligent tools that automate your marketing, nurture leads, and drive growth on autopilot.",
    color: "from-[oklch(0.65_0.15_230)] to-[oklch(0.58_0.22_345)]",
  },
  {
    icon: Zap,
    title: "Custom Solutions",
    description:
      "Bespoke software solutions designed specifically for your unique business challenges.",
    color: "from-[oklch(0.58_0.22_345)] to-[oklch(0.48_0.24_355)]",
  },
  {
    icon: Code,
    title: "API Integration",
    description:
      "Connect your tools and systems seamlessly with custom API integrations and workflows.",
    color: "from-[oklch(0.55_0.18_240)] to-[oklch(0.65_0.15_230)]",
  },
  {
    icon: Palette,
    title: "Brand & Design",
    description:
      "Stunning visual identities and user experiences that make your brand unforgettable.",
    color: "from-[oklch(0.68_0.20_335)] to-[oklch(0.58_0.22_345)]",
  },
];

function ServiceCard({ service, index }: { service: typeof services[0]; index: number }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      style={{ willChange: "transform, opacity" }}
      className="group relative"
    >
      <div className="relative h-full p-8 rounded-2xl bg-card border border-border hover:border-primary/50 transition-all duration-300 hover:shadow-xl hover:shadow-primary/10">
        {/* Gradient background on hover */}
        <div
          className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${service.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300`}
        />

        {/* Icon */}
        <motion.div
          className={`relative w-14 h-14 rounded-xl bg-gradient-to-br ${service.color} p-3 mb-6`}
          whileHover={{ scale: 1.1, rotate: 5 }}
          transition={{ type: "spring", stiffness: 400, damping: 10 }}
        >
          <service.icon className="w-full h-full text-white" />
        </motion.div>

        {/* Content */}
        <h3 className="text-2xl font-bold mb-4 text-card-foreground group-hover:text-primary transition-colors">
          {service.title}
        </h3>
        <p className="text-muted-foreground leading-relaxed">{service.description}</p>

        {/* Hover indicator */}
        <motion.div
          className="absolute bottom-8 right-8 w-8 h-8 rounded-full bg-gradient-to-br from-primary to-secondary opacity-0 group-hover:opacity-100 transition-opacity"
          initial={{ scale: 0 }}
          whileHover={{ scale: 1 }}
        >
          <svg
            className="w-full h-full text-white p-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M14 5l7 7m0 0l-7 7m7-7H3"
            />
          </svg>
        </motion.div>
      </div>
    </motion.div>
  );
}

export default function ServicesSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="services" className="py-24 md:py-32 bg-background" ref={ref}>
      <div className="container">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8 }}
        >
          <motion.span
            className="inline-block px-4 py-2 rounded-full bg-primary/10 text-primary font-semibold text-sm mb-4"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.6 }}
          >
            Our Services
          </motion.span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            Everything Your Business{" "}
            <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              Needs to Thrive
            </span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            From concept to launch, we build custom solutions that scale with your ambitions.
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <ServiceCard key={service.title} service={service} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
}
